import { Badge } from '@/components/ui/badge';
import { JobPhaseBadge } from '@/components/tickets/JobPhaseBadge';
import { formatElapsedTime, formatTimeShort, getJobPhase } from '@/utils/bayScheduleHelpers';
import { cn } from '@/lib/utils';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { memo } from 'react';
import { useSharedTimer } from '@/hooks/useSharedTimer';
import { ParkingSquare } from 'lucide-react';

interface BirdseyeBayCardProps {
  bay: any;
  currentTicket: any | null;
  nextTicket: any | null;
  technician: any | null;
  technicians: any[];
  bayTypeColors: Record<string, string>;
  bayTypeLabels: Record<string, string>;
  bayCategoryColors: Record<string, string>;
  onClick?: () => void;
  moveTicketId?: string | null;
  isDraggingOver?: boolean;
  shopTimezone?: string;
  readOnly?: boolean;
}

// Timer display component - only this re-renders every second
const TimerDisplay = memo(({ actualStartTime, status, timerStatus, pauseReason }: { 
  actualStartTime: string; 
  status: string; 
  timerStatus?: string;
  pauseReason?: string;
}) => {
  const tickTimestamp = useSharedTimer();
  
  if (status === 'Paused' || timerStatus === 'paused') {
    return (
      <div className="text-center">
        <div className="text-xs font-semibold text-orange-600 dark:text-orange-400">
          ⏸ PAUSED
        </div>
        {pauseReason && (
          <div className="text-[10px] text-orange-500 dark:text-orange-400 truncate">
            {pauseReason}
          </div>
        )}
      </div>
    );
  }
  
  return (
    <div className="text-lg font-mono font-bold text-center text-primary">
      ⏱ {formatElapsedTime(new Date(actualStartTime), tickTimestamp)}
    </div>
  );
});

// Draggable ticket component inside bay
const DraggableTicket = memo(({ 
  ticket, 
  technicians,
  isHighlighted,
  shopTimezone,
  readOnly,
}: { 
  ticket: any; 
  technicians: any[];
  isHighlighted: boolean;
  shopTimezone?: string;
  readOnly?: boolean;
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `ticket:${ticket.id}`,
    disabled: readOnly,
  });

  const { phase, partsEta, bayLocked } = getJobPhase(ticket);

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      data-draggable-ticket
      className={cn(
        "mt-2 p-2 bg-primary/10 rounded border border-primary/20 text-xs space-y-2 transition-all",
        !readOnly && "cursor-grab active:cursor-grabbing",
        isHighlighted && "ring-2 ring-blue-500 ring-offset-2 ring-offset-background animate-pulse",
        isDragging && "opacity-50"
      )}
    >
      {/* Phase Badge */}
      {phase !== 'normal' && (
        <JobPhaseBadge
          phase={phase}
          partsEta={partsEta}
          bayLocked={bayLocked}
          className="text-xs"
        />
      )}
      
      {/* Vehicle Info */}
      <div>
        <p className="font-semibold truncate">
          {ticket.vehicle?.year} {ticket.vehicle?.make} {ticket.vehicle?.model}
        </p>
        <p className="text-muted-foreground text-[10px] truncate">{ticket.customerName}</p>
        {ticket.ro_number && (
          <p className="text-muted-foreground text-[10px]">RO #{ticket.ro_number}</p>
        )}
      </div>

      {/* Live Timer */}
      {ticket.actual_start_time && (
        <div className="p-1.5 bg-background rounded border">
          <TimerDisplay 
            actualStartTime={ticket.actual_start_time}
            status={ticket.status}
            timerStatus={ticket.timer_status}
            pauseReason={ticket.pause_reason}
          />
        </div>
      )}

      {/* Start & OptiFinish */}
      <div className="flex items-center justify-between pt-1 border-t border-primary/20 text-[10px]">
        <div>
          <span className="text-muted-foreground">Start </span>
          <span className="font-medium">
            {formatTimeShort(ticket.actual_start_time || ticket.start_time, shopTimezone)}
          </span>
        </div>
        {(ticket.opti_finish || ticket.optiFinish) && (
          <div>
            <span className="text-muted-foreground">Finish </span>
            <span className="font-medium">
              {formatTimeShort(ticket.opti_finish || ticket.optiFinish, shopTimezone)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
});

export const BirdseyeBayCard = ({
  bay,
  currentTicket,
  nextTicket,
  technician,
  technicians,
  bayTypeColors,
  bayTypeLabels,
  bayCategoryColors,
  onClick,
  moveTicketId,
  shopTimezone,
  readOnly,
}: BirdseyeBayCardProps) => {
  const techName = technician?.name || 'Unassigned';
  const isAdditionalSpace = bay.bay_category && ['work-area', 'storage', 'front-of-house'].includes(bay.bay_category);
  const isLockedOut = bay.is_locked_out === true;
  const borderColor = isLockedOut 
    ? 'hsl(0, 70%, 50%)' // Red for locked out
    : isAdditionalSpace 
      ? (bayCategoryColors[bay.bay_category] || 'hsl(0, 0%, 50%)')
      : (bayTypeColors[bay.bay_type] || 'hsl(0, 0%, 50%)');

  // Make bay a droppable zone - disabled if locked out
  const { isOver, setNodeRef } = useDroppable({
    id: `bay:${bay.id}`,
    disabled: isLockedOut,
  });

  // Check if we're highlighting this bay's ticket for move mode
  const isCurrentTicketHighlighted = moveTicketId && currentTicket?.id === moveTicketId;


  return (
    <div
      ref={setNodeRef}
      data-bay-dropzone
      data-testid={`birdseye-bay-${bay.id}`}
      className={cn(
        "absolute rounded-lg p-3 transition-all hover:shadow-lg cursor-pointer",
        isAdditionalSpace ? "border-4 border-dashed" : "border-2",
        currentTicket ? "bg-card" : "bg-muted/50",
        isOver && !isLockedOut && "ring-2 ring-blue-500 ring-offset-2 ring-offset-background bg-blue-500/5",
        isLockedOut && "opacity-75 bg-destructive/5"
      )}
      style={{
        left: `${bay.position_x}px`,
        top: `${bay.position_y}px`,
        width: `${bay.width}px`,
        height: `${bay.height}px`,
        borderColor: isOver && !isLockedOut ? 'hsl(210, 100%, 50%)' : borderColor
      }}
      onClick={onClick}
    >
      {/* Lockout Banner */}
      {isLockedOut && (
        <div className="absolute inset-x-0 top-0 bg-destructive text-destructive-foreground text-xs font-bold py-1 px-2 rounded-t-lg text-center">
          🚫 LOCKED OUT
        </div>
      )}

      {/* Bay Header */}
      <div className={cn("flex items-center justify-between mb-2", isLockedOut && "mt-5")}>
        <h3 className="font-bold text-sm truncate">{bay.name}</h3>
        <Badge 
          variant={isLockedOut ? "destructive" : isAdditionalSpace ? "outline" : "secondary"} 
          className={cn(
            "text-xs shrink-0",
            isAdditionalSpace && !isLockedOut && "border-2"
          )}
          style={{
            borderColor: isAdditionalSpace && !isLockedOut ? borderColor : undefined,
            color: isAdditionalSpace && !isLockedOut ? borderColor : undefined
          }}
        >
          {isLockedOut 
            ? (bay.lockout_reason || 'Locked')
            : isAdditionalSpace 
              ? bay.bay_category?.replace('-', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) 
              : (bayTypeLabels[bay.bay_type] || bay.bay_type)}
        </Badge>
      </div>

      {/* Technician */}
      <p className="text-xs text-muted-foreground mb-2">👤 {techName}</p>

      {/* Current Vehicle (if occupied) - now draggable */}
      {currentTicket ? (
        <DraggableTicket 
          ticket={currentTicket}
          technicians={technicians}
          isHighlighted={!!isCurrentTicketHighlighted}
          shopTimezone={shopTimezone}
          readOnly={readOnly}
        />
      ) : (
        /* Empty Bay - Show Next Upcoming */
        <div className="mt-2 p-3 bg-muted/30 rounded border border-muted text-xs">
          <p className="text-center text-muted-foreground font-semibold mb-2">Empty</p>
          
          {nextTicket ? (
            <div className="space-y-1">
              <p className="text-[10px] text-muted-foreground">Next:</p>
              <p className="font-medium truncate">
                {nextTicket.vehicle?.year} {nextTicket.vehicle?.make} {nextTicket.vehicle?.model}
              </p>
              <div className="flex items-center gap-1">
                <p className="text-muted-foreground text-[10px] truncate flex-1">{nextTicket.customerName}</p>
                {nextTicket.parking_spot?.label && (
                  <Badge variant="secondary" className="text-[9px] gap-0.5 px-1 py-0 shrink-0">
                    <ParkingSquare className="h-2.5 w-2.5" />
                    P{nextTicket.parking_spot.label}
                  </Badge>
                )}
              </div>
              <div className="flex items-center justify-between pt-1 border-t border-muted text-[10px]">
                <div>
                  <span className="text-muted-foreground">Start </span>
                  <span className="font-medium">
                    {formatTimeShort(nextTicket.start_time || nextTicket.scheduled_start_time, shopTimezone)}
                  </span>
                </div>
                {(nextTicket.opti_finish || nextTicket.optiFinish) && (
                  <div>
                    <span className="text-muted-foreground">Finish </span>
                    <span className="font-medium">
                      {formatTimeShort(nextTicket.opti_finish || nextTicket.optiFinish, shopTimezone)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className="text-center text-muted-foreground text-[10px]">No upcoming tickets</p>
          )}
        </div>
      )}
    </div>
  );
};
